/**
 * Web stand-in for `react-native-create-thumbnail`.
 *
 * Grabs a frame from a video URL with a <video> + <canvas>, which is the
 * browser equivalent of the native thumbnail extractor.
 */

export type ThumbnailOptions = {
  url: string;
  timeStamp?: number;
  format?: 'jpeg' | 'png';
  cacheName?: string;
};

export type ThumbnailResult = {
  path: string;
  size: number;
  mime: string;
  width: number;
  height: number;
};

export function createThumbnail(options: ThumbnailOptions): Promise<ThumbnailResult> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.preload = 'metadata';
    video.muted = true;
    video.src = options.url;

    const fail = () => reject(new Error(`Unable to read a frame from ${options.url}`));

    video.onerror = fail;
    video.onloadeddata = () => {
      // timeStamp is milliseconds in the native module; <video> wants seconds.
      video.currentTime = Math.min((options.timeStamp ?? 1000) / 1000, video.duration || 0);
    };

    video.onseeked = () => {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const context = canvas.getContext('2d');
      if (!context) {
        fail();
        return;
      }
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      const mime = options.format === 'png' ? 'image/png' : 'image/jpeg';
      canvas.toBlob(blob => {
        if (!blob) {
          fail();
          return;
        }
        resolve({
          path: URL.createObjectURL(blob),
          size: blob.size,
          mime,
          width: canvas.width,
          height: canvas.height,
        });
      }, mime, 0.9);
    };
  });
}

export default {createThumbnail};
